// פתקים — the pinboard. A note one person leaves for another, or for themselves.
//
// ONE QUESTION: what has somebody left me, and what is on the board generally. It is not
// a task list and deliberately does not become one — a note has no due date, no status
// and no assignee, because the moment it acquires any of those it is a task, and tasks
// have their own board with a bell behind it. A note is something said, not something to
// produce.
//
// THE TOGGLE IS A VIEW, NOT A FETCH. Both halves arrive in the one list below and the
// browser splits them, which is the pattern the clients and retainers screens use:
// switching is instant, and the half you are not looking at is provably the same rows.
// "שלי" means ADDRESSED TO ME (`for_user_id`), which is what makes this a board rather
// than an outbox — a note you wrote for somebody else lives on THEIR board and is one
// click away under "של כולם".
//
// EDITING AND DELETING ARE THE AUTHOR'S, and the buttons simply are not rendered on
// somebody else's note. That is cosmetic, exactly as every other affordance in this
// client is: the server refuses it (403 `not_note_author`) whatever the browser draws.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useAuth } from '../../context/AuthContext';
import { notesApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import ViewToggle from '../../components/ViewToggle';
import EmptyState from '../../components/EmptyState';
import DateText from '../../components/DateText';
import NoteModal from './NoteModal';
import NoteDeleteModal from './NoteDeleteModal';

// Ids arrive from the API as numbers and from a form as strings; every ownership test on
// this screen goes through one comparison so the two can never disagree about a match.
const isSame = (a, b) => String(a) === String(b);

// A note that has actually been changed since it was written. TIMESTAMP resolution is a
// second, and the two columns are set in the same statement on insert — so an equal pair
// means "never edited" rather than "edited within the second".
const wasEdited = (note) => note.updated_at && note.updated_at !== note.created_at;

export default function Notes() {
  const { partner: me } = useAuth();
  const { data, loading, error, reload } = useAsync(() => notesApi.list());
  // The default half is YOUR OWN, as on every other two-state toggle in the app: the
  // narrower view first, the wider one one click away.
  const [showAll, setShowAll] = useState(false);
  // The note being written or edited — the row itself, not an id, because the form is
  // seeded from it. `{}` is a new note, which is how the other modals spell it too.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const notes = data || [];
  const rows = showAll ? notes : notes.filter((n) => isSame(n.for_user_id, me.id));

  return (
    <>
      <PageHeader title={he.notes.title} spread>
        <ViewToggle
          value={showAll}
          onChange={setShowAll}
          offLabel={he.notes.mine}
          onLabel={he.notes.all}
        />
        <Button onClick={() => setEditing({})}>{he.notes.newNote}</Button>
      </PageHeader>

      <AsyncBoundary loading={loading} error={error}>
        {rows.length === 0 ? (
          // Two different kinds of nothing: an empty board of your own is not the same
          // fact as an empty board altogether, and the wording says which one this is.
          <EmptyState text={showAll ? he.notes.emptyAll : he.notes.emptyMine} />
        ) : (
          <div className="grid cols-3">
            {rows.map((note) => {
              const mine = isSame(note.created_by_user_id, me.id);
              return (
                <div key={note.id} className="card note-card">
                  <div className="note-card-head">
                    <strong>{note.title}</strong>
                    {mine && (
                      <div className="note-card-actions">
                        <Button small variant="subtle" onClick={() => setEditing(note)}>
                          {he.common.edit}
                        </Button>
                        <Button small variant="subtle" onClick={() => setDeleting(note)}>
                          {he.common.delete}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* pre-wrap because the note is whatever somebody typed, line breaks
                      included — the content is opaque here exactly as it is in the
                      database, and reflowing it would rewrite what was said. */}
                  <p className="note-card-body">{note.body}</p>

                  {/* Who wrote it and whose board it is on. Both are shown on both tabs:
                      on a shared board "who said this" is half the message, and on the
                      "של כולם" tab the recipient is the only thing telling two otherwise
                      identical notes apart. */}
                  <div className="muted small-note">
                    {he.notes.from} {note.created_by_name}
                    {' · '}
                    {he.notes.to} {note.for_user_name}
                    {' · '}
                    <DateText value={note.created_at} />
                    {wasEdited(note) && <> {' · '}{he.notes.edited}</>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AsyncBoundary>

      {editing && (
        <NoteModal
          note={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {deleting && (
        <NoteDeleteModal
          note={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            await notesApi.remove(deleting.id);
            setDeleting(null);
            reload();
          }}
        />
      )}
    </>
  );
}
